import React, { ReactNode } from 'react';
import { PageContainer } from './PageContainer';
import { PageHeader } from './PageHeader';
import { PageTabs, TabItem } from './PageTabs';
import { PageContent, PageContentProps } from './PageContent';

export interface PageDetailLayoutProps extends Partial<PageContentProps> {
  // Container props
  className?: string;
  
  // Header props
  title: string | ReactNode;
  description?: string | ReactNode;
  subtitle?: string | ReactNode;
  headerActions?: ReactNode;
  headerExtra?: ReactNode;
  actions?: ReactNode;
  badge?: ReactNode;
  image?: ReactNode | string;

  // Cảnh báo (Alert/Warning) ngay dưới Header
  alert?: ReactNode;
  
  // Tabs props
  tabs?: TabItem[];
  activeTab?: string;
  onTabChange?: (val: string) => void;
  
  // Content
  children: ReactNode;
}

/**
 * PageDetailLayout - Siêu Component chuyên dụng cho các trang Chi tiết (VD: Chi tiết dự án, Chi tiết tài sản)
 * Áp dụng 100% chuẩn UX DNA từ 03-1-SkillDetailLayout.md
 */
export function PageDetailLayout({
  className,
  title,
  description,
  subtitle,
  headerActions,
  headerExtra,
  actions,
  badge,
  image,
  alert,
  tabs,
  activeTab,
  onTabChange,
  children,
  isLoading,
  error,
  isEmpty,
  emptyTitle,
  emptyDescription,
  emptyIcon,
  onRetry,
  currentPage,
  totalPages,
  onPageChange
}: PageDetailLayoutProps) {
  
  return (
    <PageContainer className={className}>
      
      {/* 1. Header tĩnh (Bối cảnh, Tên đối tượng, Quick Actions) */}
      <PageHeader 
        title={title} 
        description={description ?? subtitle} 
        actions={headerActions ?? actions}
        badge={badge}
        image={image}
      >
        {headerExtra}
      </PageHeader>
      
      {/* 2. Cảnh báo khẩn cấp (Nếu đối tượng đang có lỗi/trễ hạn) */}
      {alert && (
        <div className="mb-6">
          {alert}
        </div>
      )}

      {/* 3. Tab Navigation (Điều hướng rẽ nhánh) */}
      {tabs && tabs.length > 0 && activeTab && onTabChange && (
        <PageTabs 
          tabs={tabs} 
          activeTab={activeTab} 
          onChange={onTabChange} 
        />
      )}
      
      {/* 4. Nội dung của Tab hiện tại (Có thể dùng <Outlet /> nếu dùng React Router) */}
      <PageContent
        isLoading={isLoading} 
        error={error} 
        isEmpty={isEmpty}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyIcon={emptyIcon}
        onRetry={onRetry}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
      >
        {children}
      </PageContent>

    </PageContainer>
  );
}

// Hỗ trợ Compound Component Pattern
PageDetailLayout.Container = PageContainer;
PageDetailLayout.Header = PageHeader;
PageDetailLayout.Tabs = PageTabs;
PageDetailLayout.Content = PageContent;
